"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "DuplicateDetectionService", {
    enumerable: true,
    get: function() {
        return DuplicateDetectionService;
    }
});
const _common = require("@nestjs/common");
const _nocodbsdk = require("nocodb-sdk");
const _models = require("~/models");
const _uniqueConstraintHelpers = require("~/helpers/uniqueConstraintHelpers");
const _catchError = require("~/helpers/catchError");
const _NcConnectionMgrv2 = /*#__PURE__*/ _interop_require_default(require("~/utils/common/NcConnectionMgrv2"));
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
let DuplicateDetectionService = class DuplicateDetectionService {
    /**
   * Checks for duplicate non-empty values in a column
   * @param context - NocoDB context
   * @param column - Column to check
   * @param excludeRowId - Row ID to exclude from duplicate check (for updates)
   * @returns object with hasDuplicates flag and count
   */ async checkForDuplicates(context, column, excludeRowId) {
        const model = await column.getModel(context);
        const source = await _models.Source.get(context, model.source_id);
        const knex = await _NcConnectionMgrv2.default.get(source);
        // Get schema-qualified table name
        let tableName = model.table_name;
        if (source.type === 'pg') {
            var _source_isMeta;
            // For PostgreSQL, include schema if available
            let schema;
            if (source == null ? void 0 : (_source_isMeta = source.isMeta) == null ? void 0 : _source_isMeta.call(source, true, 1)) {
                var _source_getConfig;
                schema = (_source_getConfig = source.getConfig()) == null ? void 0 : _source_getConfig.schema;
            } else {
                var _source_getConfig_searchPath, _source_getConfig1;
                schema = (_source_getConfig1 = source.getConfig()) == null ? void 0 : (_source_getConfig_searchPath = _source_getConfig1.searchPath) == null ? void 0 : _source_getConfig_searchPath[0];
            }
            if (schema) {
                tableName = `${schema}.${tableName}`;
            }
        } else if (source.type === 'snowflake') {
            var _source_getConfig2;
            // For Snowflake, include database and schema
            const config = ((_source_getConfig2 = source.getConfig()) == null ? void 0 : _source_getConfig2.connection) || source.getConfig();
            if ((config == null ? void 0 : config.database) && (config == null ? void 0 : config.schema)) {
                tableName = `${config.database}.${config.schema}.${tableName}`;
            }
        }
        const columnName = column.column_name;
        const primaryKey = model.primaryKey;
        // If excludeRowId is provided but no primary key exists, we can't exclude a specific row
        if (excludeRowId && !primaryKey) {
            throw _catchError.NcError.get(context).internalServerError('Cannot exclude row: Primary key not found. Tables without primary keys cannot use row exclusion in duplicate checks.');
        }
        // Use GROUP BY with COUNT to find duplicates directly in the database
        // This is much more efficient than fetching all records
        // Limit to 1 for existence check - we just need to know if any duplicates exist
        let query = knex(tableName).select(columnName).count('* as count').whereNotNull(columnName).groupBy(columnName).havingRaw('COUNT(*) > 1').limit(1); // Limit to 1 to avoid fetching all duplicates
        // Exclude specific row if provided (for update operations)
        if (excludeRowId && primaryKey) {
            query = knex(tableName).select(columnName).count('* as count').whereNotNull(columnName).where(primaryKey.column_name, '!=', excludeRowId).groupBy(columnName).havingRaw('COUNT(*) > 1').limit(1);
        }
        const results = await query;
        const hasDuplicates = results.length > 0;
        // For count, use a simpler approach: total rows minus distinct values
        // This gives us the number of duplicate rows
        let duplicateCount = 0;
        if (hasDuplicates) {
            // Count total non-null, non-empty rows
            const totalQuery = knex(tableName).count('* as total').whereNotNull(columnName);
            // Count distinct non-null, non-empty values
            const distinctQuery = knex(tableName).select(knex.raw(`COUNT(DISTINCT ??) as distinct_count`, [
                columnName
            ])).whereNotNull(columnName);
            // Apply excludeRowId if provided
            if (excludeRowId && primaryKey) {
                totalQuery.where(primaryKey.column_name, '!=', excludeRowId);
                distinctQuery.where(primaryKey.column_name, '!=', excludeRowId);
            }
            const [totalResult, distinctResult] = await Promise.all([
                totalQuery.first(),
                distinctQuery.first()
            ]);
            const total = parseInt(String((totalResult == null ? void 0 : totalResult.total) || '0'), 10);
            const distinct = parseInt(String((distinctResult == null ? void 0 : distinctResult.distinct_count) || '0'), 10);
            // Duplicate count = total rows - distinct values
            // (each distinct value should appear once, so extra rows are duplicates)
            duplicateCount = total - distinct;
        }
        return {
            hasDuplicates,
            count: duplicateCount
        };
    }
    // keeping this function for future reference(bulk op or non-pg dbs), at the moment it's not used anywhere
    /**
   * Validates that a value doesn't violate unique constraint
   * @param context - NocoDB context
   * @param column - Column with unique constraint
   * @param value - Value to validate
   * @param excludeRowId - Row ID to exclude from duplicate check (for updates)
   * @returns true if value is unique
   */ async validateUniqueValue(context, column, value, excludeRowId) {
        // Allow empty values (null, undefined, empty string)
        if (value === null || value === undefined || value === '') {
            return true;
        }
        const model = await column.getModel(context);
        const source = await _models.Source.get(context, model.source_id);
        const knex = await _NcConnectionMgrv2.default.get(source);
        // Get schema-qualified table name
        let tableName = model.table_name;
        if (source.type === 'pg') {
            var _source_isMeta;
            // For PostgreSQL, include schema if available
            let schema;
            if (source == null ? void 0 : (_source_isMeta = source.isMeta) == null ? void 0 : _source_isMeta.call(source, true, 1)) {
                var _source_getConfig;
                schema = (_source_getConfig = source.getConfig()) == null ? void 0 : _source_getConfig.schema;
            } else {
                var _source_getConfig_searchPath, _source_getConfig1;
                schema = (_source_getConfig1 = source.getConfig()) == null ? void 0 : (_source_getConfig_searchPath = _source_getConfig1.searchPath) == null ? void 0 : _source_getConfig_searchPath[0];
            }
            if (schema) {
                tableName = `${schema}.${tableName}`;
            }
        } else if (source.type === 'snowflake') {
            var _source_getConfig2;
            // For Snowflake, include database and schema
            const config = ((_source_getConfig2 = source.getConfig()) == null ? void 0 : _source_getConfig2.connection) || source.getConfig();
            if ((config == null ? void 0 : config.database) && (config == null ? void 0 : config.schema)) {
                tableName = `${config.database}.${config.schema}.${tableName}`;
            }
        }
        const columnName = column.column_name;
        const primaryKey = model.primaryKey;
        // If excludeRowId is provided but no primary key exists, we can't exclude a specific row
        if (excludeRowId && !primaryKey) {
            throw _catchError.NcError.get(context).internalServerError('Cannot exclude row: Primary key not found. Tables without primary keys cannot use row exclusion in unique value validation.');
        }
        const normalizedValue = (0, _uniqueConstraintHelpers.normalizeValueForUniqueCheck)(value, column.uidt);
        if (normalizedValue === null) {
            return true; // Empty values are always allowed
        }
        let query = knex(tableName).count('* as count');
        // Build query based on field type
        if ([
            _nocodbsdk.UITypes.SingleLineText,
            _nocodbsdk.UITypes.LongText,
            _nocodbsdk.UITypes.Email,
            _nocodbsdk.UITypes.PhoneNumber,
            _nocodbsdk.UITypes.URL
        ].includes(column.uidt)) {
            // Exact comparison for text fields
            query = query.where(columnName, String(value));
        } else {
            // Exact comparison for other types
            query = query.where(columnName, value);
        }
        if (excludeRowId && primaryKey) {
            query = query.where(primaryKey.column_name, '!=', excludeRowId);
        }
        const result = await query.first();
        const count = parseInt(String((result == null ? void 0 : result.count) || '0'), 10);
        return count === 0;
    }
    /**
   * Gets list of duplicate values for a column (useful for error reporting)
   * @param context - NocoDB context
   * @param column - Column to check
   * @param limit - Maximum number of duplicate examples to return
   * @returns array of duplicate values
   */ async getDuplicateValues(context, column, limit = 10) {
        const model = await column.getModel(context);
        const source = await _models.Source.get(context, model.source_id);
        const knex = await _NcConnectionMgrv2.default.get(source);
        // Get schema-qualified table name
        let tableName = model.table_name;
        if (source.type === 'pg') {
            var _source_isMeta;
            // For PostgreSQL, include schema if available
            let schema;
            if (source == null ? void 0 : (_source_isMeta = source.isMeta) == null ? void 0 : _source_isMeta.call(source, true, 1)) {
                var _source_getConfig;
                schema = (_source_getConfig = source.getConfig()) == null ? void 0 : _source_getConfig.schema;
            } else {
                var _source_getConfig_searchPath, _source_getConfig1;
                schema = (_source_getConfig1 = source.getConfig()) == null ? void 0 : (_source_getConfig_searchPath = _source_getConfig1.searchPath) == null ? void 0 : _source_getConfig_searchPath[0];
            }
            if (schema) {
                tableName = `${schema}.${tableName}`;
            }
        } else if (source.type === 'snowflake') {
            var _source_getConfig2;
            // For Snowflake, include database and schema
            const config = ((_source_getConfig2 = source.getConfig()) == null ? void 0 : _source_getConfig2.connection) || source.getConfig();
            if ((config == null ? void 0 : config.database) && (config == null ? void 0 : config.schema)) {
                tableName = `${config.database}.${config.schema}.${tableName}`;
            }
        }
        const columnName = column.column_name;
        const query = knex(tableName).select(columnName).count('* as count').whereNotNull(columnName).groupBy(columnName).having('count', '>', 1).orderBy('count', 'desc').limit(limit);
        const results = await query;
        return results.map((row)=>({
                value: row[columnName],
                count: parseInt(row.count)
            }));
    }
};
DuplicateDetectionService = _ts_decorate([
    (0, _common.Injectable)()
], DuplicateDetectionService);
